import {put, call} from 'redux-saga/effects';
import Matter from "../../entities/matter";
import * as Actions from "../actions/matterActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type MatterAction = {
    matterId: string;
    matter: Matter;
    matters: Matter[];
};

export function* fetchOneMatter(action: MatterAction) {
	try {
		const matter: Matter = yield call(api.findOne, 'matter', action.matterId);

		// Now we can update the user interface
		yield put({type: Actions.MATTER_FETCH_ONE_SUCCESS, matterId: action.matterId, matter});
	}
	catch(err) {
		yield put({type: Actions.MATTER_FETCH_ONE_FAILURE, matterId: action.matterId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {MatterAction} action
 */
export function* saveMatter(action: MatterAction) {
    // console.debug('⮂ MATTER_SAVE saga', action.matter?.id);
    
	if (action.matter === undefined || action.matter === null) {
		console.error('saveMatter received a null');
		throw new Error('saveMatter received a null');
	}

	if (!(action.matter instanceof Matter)) {
		action.matter = new Matter(action.matter);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'matter', action.matter);
		if (addResult.isErr()) {
			console.error(`⮂ saveMatter: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.MATTER_SAVE_FAILURE, matter: action.matter, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.MATTER_SAVE, matter: action.matter});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'matter', action.matter.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.MATTER_SAVE_FAILURE, matter: action.matter, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {MatterAction} action
 * @param {Matter[]} action.matters
 */
export function* saveManyMatters(action: MatterAction) {
    // console.debug('⮂ MATTER_SAVE_MANY saga', action.matters.map(matter => matter.id).join(','));
    
    if (!Array.isArray(action.matters)) {
        console.error('saveManyMatters expected an array, received', action.matters);
        throw new Error('saveManyMatters expected an array');
    }

	try {
		const toSave: Matter[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.matters.length; i++) {
			const matter = action.matters[i];
			matter.persisted = false;
			toSave.push(matter);
		}

		const addResult = yield call(idb.add, 'matter', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyMatters: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.MATTER_SAVE_MANY_FAILURE, matters: action.matters, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.MATTER_SAVE_MANY, matters: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.MATTER_SAVE_MANY_FAILURE, matters: action.matters, status: 500, err});

		console.error(`Error saving ${action.matters.length} matters to database`, err);
	}
}

/**
 * @param {MatterAction} action
 */
export function* removeMatter(action: MatterAction) {
    const userId = getCurrentUserId();
	if (!userId || action.matter === undefined || action.matter === null || !(action.matter instanceof Matter)) {
			console.error('removeMatter expected type Matter, received', action.matter);
			throw new Error('removeMatter expected type Matter');
		}

	try {
		// Mark as deleted
		action.matter.status = 'D';
		action.matter.addedOn = newHRTimestamp();
		action.matter.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'matter', action.matter);
		if (addResult.isErr()) {
			console.error(`⮂ removeMatter: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.MATTER_REMOVE_FAILURE, matter: action.matter, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.MATTER_REMOVE, matter: action.matter});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.MATTER_REMOVE_FAILURE, matter: action.matter, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Matter.
	*
	* @param {MatterAction} action
	*/
export function* restoreMatter(action: MatterAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreMatter expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.matter === undefined || action.matter === null) {
        console.error('restoreMatter expected a Matter, received null');
        throw new Error('restoreMatter expected a Matter, received null');
    }
    else if (!(action.matter instanceof Matter)) action.matter = new Matter(action.matter); 
    
	try {
		// Mark as restored
		action.matter.status = 'U';
		action.matter.addedOn = newHRTimestamp();
		action.matter.addedBy = userId;
		action.matter.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'matter', action.matter);
		if (addResult.isErr()) {
			console.error(`⮂ restoreMatter: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.MATTER_RESTORE_FAILURE, matter: action.matter, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.MATTER_RESTORE, matter: action.matter});
	}
	catch(err) {
		yield put({type: Actions.MATTER_RESTORE_FAILURE, matter: action.matter, status: 500, err});

		console.error('Error restoring from database', err);
	}
}